import React, { useState, useEffect, useMemo } from "react";
import { Modal, Button } from "react-bootstrap";
import moment from "moment";
import styled from "styled-components";
import { putViaje } from "../helpers/rutaViajes";
import { getEstaciones } from "../helpers/rutaEstaciones";
import { getCajas } from "../helpers/rutaCajas";

const BotonForm = styled.button.attrs({
  type: "button",
})`
  background-color: #044a78ff;
  color: white;
  padding: 0.45rem 0.9rem;
  width: 120px;
  border: none;
  font-size: 0.9rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #06345fff;
  }
`;

const initialState = {
  nroViaje: "",
  fechaInicio: "",
  fechaFin: "",
  tarifaPorKm: 130,
  kmInicioTablero: '',
  kmFinTablero: '',
  observaciones: "",
  tramos: [],
  combustible: [],
  viaticos: [],
  adelantos: [],
  gastos: [],
  totalKm: 0,
  totalCombustible: 0,
  totalGastos: 0,
  totalViaticos: 0,
  totalAdelantos: 0,
  liquidacionFinal: 0,
};

const SMALL_LABEL = { fontSize: "0.9rem", marginBottom: "0.2rem" };
const SMALL_INPUT = { fontSize: "0.9rem" };
const SECTION_TITLE = {
  color: "blue",
  fontSize: "1.1rem",
  fontWeight: "600",
  marginTop: "1rem",
};

const METODOS_PAGO_VALIDOS = [
  "Efectivo",
  "YPF Ruta",
  "Transferencia",
  "Cta. Cte.",
  "Sin Pago",
];

const getId = (v) => {
  if (!v) return "";
  if (typeof v === "string") return v;
  if (v?.$oid) return v.$oid;
  if (typeof v?._id === "string") return v._id;
  if (v?._id?.$oid) return v._id.$oid;
  if (typeof v?.id === "string") return v.id;
  return "";
};

const normTxt = (v) =>
  String(v || "")
    .trim()
    .normalize("NFD")
    .replace(/\p{Diacritic}/gu, "")
    .toUpperCase();

const normalizarEstaciones = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.estaciones)) return resp.estaciones;
  if (Array.isArray(resp.data?.estaciones)) return resp.data.estaciones;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const normalizarCajas = (resp) => {
  if (!resp) return [];
  if (Array.isArray(resp)) return resp;
  if (Array.isArray(resp.cajas)) return resp.cajas;
  if (Array.isArray(resp.data?.cajas)) return resp.data.cajas;
  if (Array.isArray(resp.data)) return resp.data;
  return [];
};

const getCajaLabel = (caja) => {
  if (!caja) return "Sin caja";
  if (typeof caja === "string") return caja;

  return [
    caja?.descripcion,
    caja?.codcaj ? `N° ${caja.codcaj}` : "",
  ]
    .filter(Boolean)
    .join(" - ");
};

const getEstacionLabel = (est) =>
  [est?.estacion, est?.lugar, est?.codest].filter(Boolean).join(" - ");

const getLegacyStationLabel = (rawEstacion) => {
  if (!rawEstacion) return "";
  if (typeof rawEstacion === "string") return rawEstacion.trim();

  return (
    rawEstacion?.estacion ||
    rawEstacion?.nombre ||
    rawEstacion?.descripcion ||
    rawEstacion?.lugar ||
    rawEstacion?.codest ||
    ""
  );
};

const resolveEstacionId = (rawEstacion, estaciones = []) => {
  const directo = getId(rawEstacion);
  if (directo && directo.length === 24) return directo;

  const texto = getLegacyStationLabel(rawEstacion);
  if (!texto) return "";

  const textoNorm = normTxt(texto);

  const matchExacto = estaciones.find((est) => {
    const nombre = normTxt(est?.estacion);
    const lugar = normTxt(est?.lugar);
    const codest = normTxt(est?.codest);
    const completo = normTxt(getEstacionLabel(est));

    return (
      textoNorm === nombre ||
      textoNorm === lugar ||
      textoNorm === codest ||
      textoNorm === completo
    );
  });

  if (matchExacto) return getId(matchExacto);

  const matchParcial = estaciones.find((est) => {
    const nombre = normTxt(est?.estacion);
    const lugar = normTxt(est?.lugar);
    const codest = normTxt(est?.codest);
    const completo = normTxt(getEstacionLabel(est));

    return (
      (nombre && (nombre.includes(textoNorm) || textoNorm.includes(nombre))) ||
      (lugar && (lugar.includes(textoNorm) || textoNorm.includes(lugar))) ||
      (codest && (codest.includes(textoNorm) || textoNorm.includes(codest))) ||
      (completo && (completo.includes(textoNorm) || textoNorm.includes(completo)))
    );
  });

  return matchParcial ? getId(matchParcial) : "";
};

const getEmailUsuarioActual = () => {
  try {
    const token =
      localStorage.getItem("token");

    if (!token) return "";

    const payload =
      token.split(".")[1];

    if (!payload) return "";

    const base64 = payload
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(
        Math.ceil(payload.length / 4) * 4,
        "="
      );

    const decoded =
      JSON.parse(
        decodeURIComponent(
          atob(base64)
            .split("")
            .map(
              (c) =>
                "%" +
                c.charCodeAt(0)
                  .toString(16)
                  .padStart(2, "0")
            )
            .join("")
        )
      );

    const user =
      decoded.usuario ||
      decoded.user ||
      decoded;

    return String(
      user?.email || ""
    )
      .trim()
      .toLowerCase();
  } catch {
    return "";
  }
};

export default function ModalFormViaje({ viaje, handleClose, show }) {
  const [form, setForm] = useState(initialState);
  const [showSuccess, setShowSuccess] = useState(false);
  const [estaciones, setEstaciones] = useState([]);
  const [cajas, setCajas] = useState([]);

  const puedeEliminarAdelantoImpactado =
  getEmailUsuarioActual() ===
  "pablonsaya@hotmail.com";

  const estacionesOrdenadas = useMemo(() => {
    return [...estaciones]
      .filter((e) => e?.activo !== false)
      .sort((a, b) =>
        getEstacionLabel(a).localeCompare(getEstacionLabel(b), "es", {
          sensitivity: "base",
        })
      );
  }, [estaciones]);

  const cajasOrdenadas = useMemo(() => {
    return [...cajas]
      .filter((c) => c?.activo !== false)
      .sort((a, b) =>
        getCajaLabel(a).localeCompare(getCajaLabel(b), "es", {
          sensitivity: "base",
        })
      );
  }, [cajas]);

  useEffect(() => {
    const cargarEstaciones = async () => {
      try {
        const resp = await getEstaciones();
        const lista = normalizarEstaciones(resp);
        setEstaciones(lista);
      } catch (error) {
        console.error("❌ Error al cargar estaciones:", error);
        setEstaciones([]);
      }
    };

    cargarEstaciones();
  }, []);

  useEffect(() => {
    const cargarCajas = async () => {
      try {
        const resp = await getCajas();
        const lista = normalizarCajas(resp);
        setCajas(lista);
      } catch (error) {
        console.error("❌ Error al cargar cajas:", error);
        setCajas([]);
      }
    };

    cargarCajas();
  }, []); 

  useEffect(() => {
    if (!viaje?._id) return;

    const tramosFormateados = (viaje.tramos || []).map((t) => ({
      ...t,
      fecha: t.fecha ? moment(t.fecha).format("YYYY-MM-DD") : "",
    }));

    const combustibleFormateado = (viaje.combustible || []).map((c) => {
      const estacionResuelta = resolveEstacionId(c.estacion, estacionesOrdenadas);
      const legacyLabel = getLegacyStationLabel(c.estacion);

      return {
        ...c,
        fecha: c.fecha ? moment(c.fecha).format("YYYY-MM-DD") : "",
        estacion:
          estacionResuelta ||
          (legacyLabel ? `__LEGACY__:${legacyLabel}` : ""),
        estacionLegacy: legacyLabel || "",
      };
    });

    setForm({
      ...initialState,
      ...viaje,
      fechaInicio: viaje.fechaInicio
        ? moment(viaje.fechaInicio).format("YYYY-MM-DD")
        : "",
      fechaFin: viaje.fechaFin
        ? moment(viaje.fechaFin).format("YYYY-MM-DD")
        : "",
      kmInicioTablero: viaje.kmInicioTablero ?? '',
      kmFinTablero: viaje.kmFinTablero ?? '',
      tramos: tramosFormateados,
      combustible: combustibleFormateado,
    });
  }, [viaje, estacionesOrdenadas]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: name === "tarifaPorKm" ? Number(value || 0) : value,
    }));
  };

  const handleArrayChange = (arrayKey, index, field, value) => {
    const updated = [...form[arrayKey]];

    if (["litros", "monto", "kmTablero"].includes(field)) {
      updated[index][field] = value === "" ? "" : Number(value);
    } else {
      updated[index][field] = value;
    }

    setForm((prev) => ({
      ...prev,
      [arrayKey]: updated,
    }));
  };

  const addItem = (arrayKey, emptyItem) => {
    setForm((prev) => ({
      ...prev,
      [arrayKey]: [...prev[arrayKey], emptyItem],
    }));
  };

  const removeItem = (arrayKey, index) => {
    const updated = form[arrayKey].filter((_, i) => i !== index);
    setForm((prev) => ({
      ...prev,
      [arrayKey]: updated,
    }));
  };

  const isValidObjectId = (v) => getId(v).length === 24;
  const isValidMetodoPago = (v) => METODOS_PAGO_VALIDOS.includes(v);

  const sanitizeCombustible = (arr) =>
    arr
      .filter((c) => c.fecha && c.kmTablero !== "")
      .map((c) => {
        const obj = {
          fecha: new Date(c.fecha),
          kmTablero: Number(c.kmTablero),
        };

        if (c.litros !== "" && !isNaN(c.litros)) obj.litros = Number(c.litros);
        if (c.monto !== "" && !isNaN(c.monto)) obj.monto = Number(c.monto);
        if (c.localidad) obj.localidad = c.localidad;
        if (isValidMetodoPago(c.metodoPago)) obj.metodoPago = c.metodoPago;

        if (isValidObjectId(c.estacion)) {
          obj.estacion = getId(c.estacion);
        }

        return obj;
  });

  const sanitizeAdelantos = (arr) =>
    (arr || []).map((a) => ({
      _id: a._id,
      descripcion: a.descripcion || "",
      monto: Number(a.monto || 0),
      caja: getId(a.caja) || null,
      movimiento: getId(a.movimiento) || null,
      impactaCaja: !!a.impactaCaja,
    }));

  const resumen = useMemo(() => {
    const totalLitros = form.combustible.reduce(
      (sum, c) => sum + Number(c.litros || 0),
      0
    );

    const kmTableros = form.combustible
      .map((c) => Number(c.kmTablero))
      .filter((km) => !isNaN(km));

    const totalKm =
      kmTableros.length >= 2
        ? Math.max(...kmTableros) - Math.min(...kmTableros)
        : 0;

    const totalCombustible = form.combustible.reduce(
      (sum, c) => sum + Number(c.monto || 0),
      0
    );

    const totalViaticos = form.viaticos.reduce(
      (sum, v) => sum + Number(v.monto || 0),
      0
    );

    const totalAdelantos = form.adelantos.reduce(
      (sum, a) => sum + Number(a.monto || 0),
      0
    );

    const totalGastos = form.gastos.reduce(
      (sum, g) => sum + Number(g.monto || 0),
      0
    );

    const promedioConsumo = totalLitros ? (totalKm / totalLitros).toFixed(2) : "-";
    const liquidacionFinal1 = totalKm * Number(form.tarifaPorKm || 0) + totalViaticos;
    const liquidacionFinal2 = totalAdelantos - totalGastos;
    const liquidacionFinal = liquidacionFinal1 - liquidacionFinal2;

    return {
      totalKm,
      totalLitros,
      totalCombustible,
      totalViaticos,
      totalAdelantos,
      totalGastos,
      promedioConsumo,
      liquidacionFinal,
    };
  }, [form]);

  const handleSubmit = async (e) => {
    e.preventDefault();

  const adelantoSinCaja = (form.adelantos || []).find(
    (a) => Number(a.monto || 0) > 0 && !getId(a.caja)
  );

  if (adelantoSinCaja) {
    alert("Todo adelanto con monto debe tener seleccionada una caja.");
    return;
  }

  const payload = {
    ...form,
    kmInicioTablero:
      form.kmInicioTablero === '' || form.kmInicioTablero == null
        ? null
        : Number(form.kmInicioTablero),

    kmFinTablero:
      form.kmFinTablero === '' || form.kmFinTablero == null
        ? null
        : Number(form.kmFinTablero),
    fechaInicio: form.fechaInicio ? new Date(form.fechaInicio) : null,
    fechaFin: form.fechaFin ? new Date(form.fechaFin) : null,
    combustible: sanitizeCombustible(form.combustible),
    adelantos: sanitizeAdelantos(form.adelantos),
  };

    try {
      const resp = await putViaje(viaje._id, payload);

      if (!resp || resp.ok === false || resp.error) {
        const mensaje =
          typeof resp?.error === "string"
            ? resp.error
            : resp?.error?.error ||
              resp?.error?.message ||
              "No se pudo guardar la rendición.";

        alert(mensaje);
        return;
      }

      setShowSuccess(true);

      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
      }, 1500);
    } catch (error) {
      console.error("❌ Error al modificar viaje:", error);
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose} size="lg">
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title style={{ fontWeight: "bold", fontSize: "1.1rem" }}>
              Editar Viaje Nro. {form.nroViaje}
            </Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="form-row">
              <div className="form-group col-md-2">
                <label style={SMALL_LABEL}>Nro de Viaje</label>
                <input
                  type="text"
                  name="nroViaje"
                  className="form-control"
                  style={SMALL_INPUT}
                  value={form.nroViaje}
                  disabled
                />
              </div>

              <div className="form-group col-md-3">
                <label style={SMALL_LABEL}>Chofer</label>
                <input
                  type="text"
                  name="chofer"
                  className="form-control"
                  style={SMALL_INPUT}
                  value={form.choferId?.nombre || form.choferId || "-"}
                  disabled
                />
              </div>

              <div className="form-group col-md-4">
                <label style={SMALL_LABEL}>Camión</label>
                <input
                  type="text"
                  name="camion"
                  className="form-control"
                  style={SMALL_INPUT}
                  value={
                    form.camionId && typeof form.camionId === "object"
                      ? `${form.camionId.camion || ""} (${form.camionId.patente || ""})`
                      : "-"
                  }
                  disabled
                />
              </div>

              <div className="form-group col-md-2">
                <label style={SMALL_LABEL}>Tarifa por KM</label>
                <input
                  type="number"
                  name="tarifaPorKm"
                  className="form-control"
                  style={{ ...SMALL_INPUT, color: "red" }}
                  value={form.tarifaPorKm}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group col-md-3">
                <label style={SMALL_LABEL}>KM Inicio Tablero</label>
                <input
                  type="number"
                  name="kmInicioTablero"
                  className="form-control"
                  min="0"
                  step="1"
                  style={{ ...SMALL_INPUT, color: "red" }}
                  value={form.kmInicioTablero ?? ""}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group col-md-3">
                <label style={SMALL_LABEL}>KM Fin Tablero</label>
                <input
                  type="number"
                  name="kmFinTablero"
                  className="form-control"
                  min="0"
                  step="1"
                  style={{ ...SMALL_INPUT, color: "red" }}
                  value={form.kmFinTablero ?? ""}
                  onChange={handleChange}
                />
              </div>
            </div>

            <h2 style={SECTION_TITLE}>Tramos</h2>

            {form.tramos.map((t, i) => (
              <div className="form-row" key={`tramo-${i}`}>
                <div className="form-group col-md-2">
                  <label style={SMALL_LABEL}>Fecha</label>
                  <input
                    className="form-control"
                    type="date"
                    style={SMALL_INPUT}
                    value={t.fecha}
                    onChange={(e) =>
                      handleArrayChange("tramos", i, "fecha", e.target.value)
                    }
                  />
                </div>

                {["origen", "destino", "remito"].map((field) => (
                  <div key={field} className="form-group col-md-3">
                    <label style={SMALL_LABEL}>
                      {field.charAt(0).toUpperCase() + field.slice(1)}
                    </label>
                    <input
                      className="form-control"
                      style={SMALL_INPUT}
                      value={t[field] || ""}
                      onChange={(e) =>
                        handleArrayChange("tramos", i, field, e.target.value)
                      }
                    />
                  </div>
                ))}

                <div className="form-group col-md-1 d-flex align-items-end">
                  <Button
                    variant="danger"
                    size="sm"
                    style={{ fontSize: "0.85rem" }}
                    onClick={() => removeItem("tramos", i)}
                  >
                    🗑
                  </Button>
                </div>
              </div>
            ))}

            <BotonForm
              onClick={() =>
                addItem("tramos", {
                  fecha: "",
                  origen: "",
                  destino: "",
                  remito: "",
                  kmInicial: 0,
                  kmFinal: 0,
                })
              }
            >
              + Tramo
            </BotonForm>

            <h2 style={SECTION_TITLE}>Cargas de Combustible</h2>

            {form.combustible.map((c, i) => (
              <div className="form-row" key={`comb-${i}`}>
                <div className="form-group col-md-2">
                  <label style={SMALL_LABEL}>Fecha</label>
                  <input
                    className="form-control"
                    style={SMALL_INPUT}
                    type="date"
                    value={c.fecha || ""}
                    onChange={(e) =>
                      handleArrayChange("combustible", i, "fecha", e.target.value)
                    }
                  />
                </div>

                <div className="form-group col-md-1">
                  <label style={SMALL_LABEL}>Litros</label>
                  <input
                    className="form-control"
                    style={SMALL_INPUT}
                    type="number"
                    value={c.litros ?? ""}
                    onChange={(e) =>
                      handleArrayChange("combustible", i, "litros", e.target.value)
                    }
                  />
                </div>

                <div className="form-group col-md-1">
                  <label style={SMALL_LABEL}>Monto</label>
                  <input
                    className="form-control"
                    style={SMALL_INPUT}
                    type="number"
                    value={c.monto ?? ""}
                    onChange={(e) =>
                      handleArrayChange("combustible", i, "monto", e.target.value)
                    }
                  />
                </div>

                <div className="form-group col-md-2">
                  <label style={SMALL_LABEL}>Pago</label>
                  <select
                    className="form-control"
                    style={SMALL_INPUT}
                    value={c.metodoPago || ""}
                    onChange={(e) =>
                      handleArrayChange("combustible", i, "metodoPago", e.target.value)
                    }
                  >
                    <option value="">Seleccionar</option>
                    {METODOS_PAGO_VALIDOS.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group col-md-2">
                  <label style={SMALL_LABEL}>Estación</label>
                  <select
                    className="form-control"
                    style={SMALL_INPUT}
                    value={c.estacion || ""}
                    onChange={(e) =>
                      handleArrayChange("combustible", i, "estacion", e.target.value)
                    }
                  >
                    <option value="">Seleccionar Estación</option>

                    {String(c.estacion || "").startsWith("__LEGACY__:") && (
                      <option value={c.estacion}>
                        ⚠ {String(c.estacion).replace("__LEGACY__:", "")}
                      </option>
                    )}

                    {estacionesOrdenadas.map((est) => (
                      <option key={getId(est)} value={getId(est)}>
                        {getEstacionLabel(est)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group col-md-1">
                  <label style={SMALL_LABEL}>Local</label>
                  <input
                    className="form-control"
                    style={SMALL_INPUT}
                    value={c.localidad || ""}
                    onChange={(e) =>
                      handleArrayChange("combustible", i, "localidad", e.target.value)
                    }
                  />
                </div>

                <div className="form-group col-md-2">
                  <label style={SMALL_LABEL}>km Tab</label>
                  <input
                    className="form-control"
                    style={SMALL_INPUT}
                    type="number"
                    value={c.kmTablero ?? ""}
                    onChange={(e) =>
                      handleArrayChange("combustible", i, "kmTablero", e.target.value)
                    }
                  />
                </div>

                <div className="form-group col-md-1 d-flex align-items-end">
                  <Button
                    variant="danger"
                    size="sm"
                    style={{ fontSize: "0.85rem" }}
                    onClick={() => removeItem("combustible", i)}
                  >
                    🗑
                  </Button>
                </div>
              </div>
            ))}

            <BotonForm
              onClick={() =>
                addItem("combustible", {
                  fecha: "",
                  litros: "",
                  monto: "",
                  metodoPago: "",
                  estacion: "",
                  localidad: "",
                  kmTablero: "",
                  estacionLegacy: "",
                })
              }
            >
              + Carga
            </BotonForm>

            {["viaticos", "adelantos", "gastos"].map((arrayKey) => {
              const esAdelanto = arrayKey === "adelantos";

              return (
                <div key={arrayKey}>
                  <h5 style={SECTION_TITLE}>
                    {arrayKey.charAt(0).toUpperCase() + arrayKey.slice(1)}
                  </h5>

                  {form[arrayKey].map((item, i) => {
                    const adelantoBloqueado =
                      esAdelanto && (!!getId(item.movimiento) || !!item.impactaCaja);

                    return (
                      <div className="form-row" key={`${arrayKey}-${i}`}>
                        <div className={`form-group ${esAdelanto ? "col-md-5" : "col-md-8"}`}>
                          <label style={SMALL_LABEL}>Descripción</label>
                          <input
                            className="form-control"
                            style={{
                              ...SMALL_INPUT,
                              backgroundColor: adelantoBloqueado ? "#e9ecef" : "white",
                            }}
                            value={item.descripcion || ""}
                            disabled={adelantoBloqueado}
                            onChange={(e) =>
                              handleArrayChange(arrayKey, i, "descripcion", e.target.value)
                            }
                          />
                        </div>

                        <div className={`form-group ${esAdelanto ? "col-md-2" : "col-md-3"}`}>
                          <label style={SMALL_LABEL}>Monto</label>
                          <input
                            className="form-control"
                            style={{
                              ...SMALL_INPUT,
                              backgroundColor: adelantoBloqueado ? "#e9ecef" : "white",
                            }}
                            type="number"
                            value={item.monto ?? 0}
                            disabled={adelantoBloqueado}
                            onChange={(e) =>
                              handleArrayChange(arrayKey, i, "monto", e.target.value)
                            }
                          />
                        </div>

                        {esAdelanto && (
                          <div className="form-group col-md-4">
                            <label style={SMALL_LABEL}>Caja</label>
                            <select
                              className="form-control"
                              style={{
                                ...SMALL_INPUT,
                                backgroundColor: adelantoBloqueado ? "#e9ecef" : "white",
                              }}
                              value={getId(item.caja)}
                              disabled={adelantoBloqueado}
                              onChange={(e) =>
                                handleArrayChange(arrayKey, i, "caja", e.target.value)
                              }
                            >
                              <option value="">Seleccionar caja</option>

                              {cajasOrdenadas.map((caja) => (
                                <option key={getId(caja)} value={getId(caja)}>
                                  {getCajaLabel(caja)} - Saldo: $
                                  {Number(caja.saldoActual || 0).toLocaleString("es-AR", {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2,
                                  })}
                                </option>
                              ))}
                            </select>

                            {adelantoBloqueado && (
                              <small style={{ color: "#198754", fontSize: "0.75rem" }}>
                                Adelanto impactado en caja. No editable.
                              </small>
                            )}
                          </div>
                        )}

                        <div className="form-group col-md-1 d-flex align-items-end">
                          <Button
                            variant="danger"
                            size="sm"
                            style={{ fontSize: "0.85rem" }}
                            disabled={
                              adelantoBloqueado &&
                              !puedeEliminarAdelantoImpactado
                            }
                            title={
                              adelantoBloqueado
                                ? puedeEliminarAdelantoImpactado
                                  ? "Eliminar adelanto y generar reversa en caja"
                                  : "Solo Pablo Saya puede eliminar un adelanto impactado en caja"
                                : "Eliminar"
                            }
                            onClick={() =>
                              removeItem(arrayKey, i)
                            }
                          >
                            🗑
                          </Button>
                        </div>
                      </div>
                    );
                  })}

                  <BotonForm
                    onClick={() =>
                      addItem(
                        arrayKey,
                        esAdelanto
                          ? { descripcion: "", monto: 0, caja: "", movimiento: null, impactaCaja: false }
                          : { descripcion: "", monto: 0 }
                      )
                    }
                  >
                    + {arrayKey.slice(0, -1)}
                  </BotonForm>
                </div>
              );
            })}

            <div className="mt-4 border-top pt-3">
              <h4 style={{ ...SECTION_TITLE, marginTop: 0 }}>Resumen</h4>
              <h5 style={{ fontSize: "0.9rem" }}>Total KM: {resumen.totalKm}</h5>
              <h5 style={{ fontSize: "0.9rem" }}>Valor KM: ${form.tarifaPorKm}</h5>
              <h5 style={{ fontSize: "0.9rem" }}>
                Valor del Viaje:{" "}
                {(resumen.totalKm * form.tarifaPorKm).toLocaleString("es-AR", {
                  style: "currency",
                  currency: "ARS",
                })}
              </h5>
              <h5 style={{ fontSize: "0.9rem" }}>
                Total Combustible:{" "}
                {resumen.totalCombustible.toLocaleString("es-AR", {
                  style: "currency",
                  currency: "ARS",
                })}
              </h5>
              <h5 style={{ fontSize: "0.9rem" }}>
                Litros Cargados: {resumen.totalLitros} L
              </h5>
              <h5 style={{ fontSize: "0.9rem" }}>
                Promedio Consumo: {resumen.promedioConsumo} km/L
              </h5>
              <h5 style={{ fontSize: "0.9rem" }}>
                Viáticos:{" "}
                {resumen.totalViaticos.toLocaleString("es-AR", {
                  style: "currency",
                  currency: "ARS",
                })}{" "}
                | Adelantos:{" "}
                {resumen.totalAdelantos.toLocaleString("es-AR", {
                  style: "currency",
                  currency: "ARS",
                })}{" "}
                | Gastos:{" "}
                {resumen.totalGastos.toLocaleString("es-AR", {
                  style: "currency",
                  currency: "ARS",
                })}
              </h5>
              <h5 style={{ fontSize: "0.95rem", fontWeight: "bold" }}>
                Total a Cobrar:{" "}
                {resumen.liquidacionFinal.toLocaleString("es-AR", {
                  style: "currency",
                  currency: "ARS",
                })}
              </h5>
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={handleClose}
              style={{ fontSize: "0.9rem" }}
            >
              Cancelar
            </Button>
            <Button type="submit" variant="dark" style={{ fontSize: "0.9rem" }}>
              Guardar
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      <Modal show={showSuccess} centered backdrop="static" keyboard={false}>
        <Modal.Body className="text-center" style={{ fontSize: "0.9rem" }}>
          <div style={{ fontSize: "2rem" }}>🖐</div>
          <h5 className="mt-2" style={{ fontSize: "0.95rem" }}>
            ¡Cambios guardados correctamente!
          </h5>
        </Modal.Body>
      </Modal>
    </>
  );
}